import {
  Button,
  ButtonTypes,
  ConsentStatuses,
  IError,
  IProfile,
  LoadingOverlay,
  Spinner,
  formatDate,
  renderConsentStatusNameFromConsentStatus,
  useToast,
} from '@comptia-sso/core';
import { ReactElement, useEffect, useState } from 'react';

// Components
import { Card, ConfirmationModal } from 'components';

// Hook.
import { useProfileConsentManagement } from 'hooks';

// Styles.
import styles from './ConsentManagement.module.scss';
import { useResendConsent } from 'hooks/admin/useResendConsent';
import { useClearConsent } from 'hooks/admin/useClearConsent';
import { IAccount } from 'types';

interface IConsentManagmeentProps {
  account: IAccount;
  profile: IProfile;
}

export const ConsentManagement = (
  props: IConsentManagmeentProps,
): ReactElement => {
  const { account, profile } = props;
  const [popToast] = useToast();
  const [resendConsent] = useResendConsent(profile?.id);
  const [clearConsent] = useClearConsent(profile?.id);
  const [isUpdatingConsent, setIsUpdatingConsent] = useState(false);
  const [isConfirmingClear, setIsConfirmingClear] = useState(false);
  const [
    profileConsentManagement,
    {
      error: profileConsentManagementError,
      isLoading: isLoadingProfileConsentManagement,
    },
  ] = useProfileConsentManagement(profile?.id);

  useEffect(() => {
    if (profileConsentManagementError) {
      popToast(
        (profileConsentManagementError as IError)?.message ||
          'An unexpected error occurred. Please try again later.',
      );
    }
  }, [profileConsentManagementError]);

  const resendPrivoConsent = async () => {
    try {
      setIsUpdatingConsent(true);
      await resendConsent();
      setIsUpdatingConsent(false);
      popToast('Consent has been resent');
    } catch (error) {
      setIsUpdatingConsent(false);
      popToast(
        (error as IError)?.message ||
          'An unexpected error occurred. Please try again later.',
      );
    }
  };

  const handleClearConsentClick = () => {
    setIsConfirmingClear(true);
  };

  const handleClearConsentCancel = () => {
    setIsConfirmingClear(false);
  };

  const handleClearConsentConfirm = async () => {
    setIsConfirmingClear(false);
    try {
      setIsUpdatingConsent(true);
      await clearConsent();
      setIsUpdatingConsent(false);
      popToast('Consent has been cleared');
    } catch (error) {
      setIsUpdatingConsent(false);
      popToast(
        (error as IError)?.message ||
          'An unexpected error occurred. Please try again later.',
      );
    }
  };

  if (!profile) return <></>;

  return (
    <section title="Consent Management">
      <header>
        <h2>Consent Information</h2>
      </header>
      <Card className={styles.content}>
        {profile && !isLoadingProfileConsentManagement ? (
          <>
            {profileConsentManagement ? (
              <>
                <div className={styles.grid}>
                  <div className={styles.column}>
                    <div className={styles.detail}>
                      <strong>Is Minor</strong>
                      <span>
                        {profileConsentManagement?.isMinor !== null
                          ? profileConsentManagement?.isMinor
                            ? 'Yes'
                            : 'No'
                          : 'N/A'}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Consent Status</strong>
                      <span>
                        {renderConsentStatusNameFromConsentStatus(
                          profileConsentManagement,
                        )}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Aged Up</strong>
                      <span>
                        {profileConsentManagement?.agedUp !== null
                          ? profileConsentManagement?.agedUp
                            ? 'Yes'
                            : 'No'
                          : 'N/A'}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Consent Notice Viewed</strong>
                      <span>
                        {profileConsentManagement?.consentNoticeViewed !== null
                          ? profileConsentManagement?.consentNoticeViewed
                            ? 'Yes'
                            : 'No'
                          : 'N/A'}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Parent/Granter Email</strong>
                      <span>
                        {profileConsentManagement?.parentEmail ?? 'N/A'}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Consent Flow Type</strong>
                      <span>
                        {profileConsentManagement?.consentFlowType
                          ? profileConsentManagement.consentFlowType
                              .charAt(0)
                              .toUpperCase() +
                            profileConsentManagement.consentFlowType.slice(1)
                          : 'N/A'}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>
                        Privo External Identifier (same as SSO Id)
                      </strong>
                      <span>
                        {profileConsentManagement.consentIdentifier != null
                          ? profile?.ssoId
                          : 'N/A'}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Privo Consent Identifier</strong>
                      <span>
                        {profileConsentManagement?.consentIdentifier ?? 'N/A'}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Privo Service Id</strong>
                      <span>{profileConsentManagement?.sid ?? 'N/A'}</span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Privo Parent/Granter Service Id</strong>
                      <span>
                        {profileConsentManagement?.parentSid ?? 'N/A'}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Consent Url</strong>
                      <span>
                        {profileConsentManagement?.consentUrl ?? 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.column}>
                    <div className={styles.detail}>
                      <strong>Last Consent Request Date</strong>
                      <span>
                        {profileConsentManagement?.lastConsentRequestDate ===
                        null
                          ? 'N/A'
                          : formatDate(
                              profileConsentManagement?.lastConsentRequestDate,
                            )}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Last Consent Request Expire Date</strong>
                      <span>
                        {profileConsentManagement?.lastConsentRequestExpireDate ===
                        null
                          ? 'N/A'
                          : formatDate(
                              profileConsentManagement?.lastConsentRequestExpireDate,
                            )}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Consent Revoked Date</strong>
                      <span>
                        {profileConsentManagement?.consentRevokedDate === null
                          ? 'N/A'
                          : formatDate(
                              profileConsentManagement?.consentRevokedDate,
                            )}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Last Consent Approved Date</strong>
                      <span>
                        {profileConsentManagement?.lastConsentApprovedDate ===
                        null
                          ? 'N/A'
                          : formatDate(
                              profileConsentManagement?.lastConsentApprovedDate,
                            )}
                      </span>
                    </div>
                    <div className={styles.detail}>
                      <strong>Last Modified</strong>
                      <span>
                        {profileConsentManagement?.lastModified === null
                          ? 'N/A'
                          : formatDate(profileConsentManagement?.lastModified)}
                      </span>
                    </div>
                  </div>
                </div>
                {account.permissions?.consentManagement &&
                profileConsentManagement?.isMinor === true ? (
                  <>
                    {profileConsentManagement?.consentStatus ===
                      ConsentStatuses.Pending && (
                      <Button
                        className={styles.button}
                        type={ButtonTypes.Button}
                        onClick={resendPrivoConsent}
                      >
                        Resend Consent Email
                      </Button>
                    )}
                  </>
                ) : null}
                {account.permissions?.consentManagement &&
                profileConsentManagement?.consentStatus ===
                  ConsentStatuses.Delete ? (
                  <Button
                    className={styles.button}
                    type={ButtonTypes.Button}
                    onClick={handleClearConsentClick}
                  >
                    Clear Consent
                  </Button>
                ) : null}
                <ConfirmationModal
                  confirmationMessage="This will clear the consent record and allow the user to restart the age verification and consent process on their next login. Are you sure you want to proceed?"
                  open={isConfirmingClear}
                  onConfirm={handleClearConsentConfirm}
                  onCancel={handleClearConsentCancel}
                />
              </>
            ) : (
              <div className={styles.noConsent}>
                <span>No Consent Record Found</span>
              </div>
            )}
          </>
        ) : (
          <Spinner className={styles.spinner} />
        )}
      </Card>
      <LoadingOverlay isOpen={isUpdatingConsent} text="Updating Consent" />
    </section>
  );
};
